import { Field, Address } from "../../../globalTypes";
import { BaseAddress, GoogleMapsLink } from "../..";
import React from "react";
import { formatCurrency, renderBadge } from "../../../utils";

const renderBaseDetailFieldValue = <T extends React.ReactNode>(
	field: Field,
	dataItem?: Address | string | number | boolean | undefined,
	isDisabled?: boolean,
	allFields?: Field[],
	allData?: object,
	currencyCharacter?: string,
) => {
	const {
		label,
		disabledClasses,
		isDoubleValue,
		secondValueTextClasses,
		valueType,
		addressLineOneTextClasses,
		otherLineTextClasses,
		truthyValue,
		falsyValue,
		isPersonaValue,
		isLink,
		linkClasses,
		isGoogleMapsLink,
		isConcatenated,
		secondValueKey,
		secondValueLabel,
		suffix,
		valueTextClasses,
	} = field || {};

	const disabledClass = isDisabled ? disabledClasses : "";

	if (Array.isArray(dataItem) && isDoubleValue) {
		return (
			<div className={`flex flex-col ${disabledClass}`}>
				<>
					<span className={disabledClass}>{dataItem[0]}</span>
					<span
						className={`${secondValueTextClasses} ${disabledClass}`}
					>
						{dataItem[1]}
					</span>
				</>
			</div>
		);
	} else if (valueType === "address") {
		return (
			<div className={`flex ${disabledClass}`}>
				<BaseAddress
					addressLines={dataItem as string[]}
					textClasses={valueTextClasses}
				/>
			</div>
		);
	} else if (valueType === "status") {
		return (
			<div className={`flex ${disabledClass}`}>
				{renderBadge(dataItem as string, "text-sm font-medium")}
			</div>
		);
	} else if (valueType === "boolean") {
		return (
			<span className={disabledClass}>
				{dataItem === true ? truthyValue : falsyValue}
			</span>
		);
	} else if (
		valueType === "currency" &&
		(typeof dataItem === "string" || typeof dataItem === "number") &&
		dataItem !== "—"
	) {
 		return (
			<span
				className={disabledClass}
			>{`${currencyCharacter} ${formatCurrency(dataItem)} ${
				suffix ? suffix : ""
			}`}</span>
		);
	} else if (Array.isArray(dataItem) && !isPersonaValue) {
		return (
			<div>
				{dataItem.length === 0 ? (
					<span>—</span>
				) : (
					<ul className={`flex flex-wrap ${disabledClass}`}>
						{dataItem.map((val, index) => (
							<li key={index} className={disabledClass}>
								<span className="text-nowrap">
									{val}
									{index < dataItem.length - 1 && (
										<span className="pr-2">, </span>
									)}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
		);
	} else if (Array.isArray(dataItem) && isPersonaValue) {
		return (
			<div>
				{dataItem.length === 0 ? (
					<span>—</span>
				) : (
					<ul className={`flex flex-wrap ${disabledClass}`}>
						{dataItem.map((val, index) => (
							<li key={index} className={disabledClass}>
								<span className="text-nowrap">
									{val.name ? val.name : val}
									{index < dataItem.length - 1 && (
										<span className="pr-2">, </span>
									)}
								</span>
							</li>
						))}
					</ul>
				)}
			</div>
		);
	} else if (isLink) {
		return (
			<a
				className={`cursor-pointer ${linkClasses} ${disabledClass}`}
				href={isLink}
			>
				{label}
			</a>
		);
	} else if (isGoogleMapsLink) {
		return (
			<GoogleMapsLink
				address={dataItem as Address}
				hoverClasses={field.hoverClasses ?? ""}
			/>
		);
	} else if (isConcatenated && dataItem && allData) {
		const secondValue = allFields?.find((singleField) => {
			return singleField.valueKey === isConcatenated;
		});

		let secondValueKey = secondValue?.valueKey as keyof typeof dataItem;
		return (
			<span className={disabledClass}>
				{dataItem + " " + allData[secondValueKey]}
			</span>
		);
	} else if (secondValueKey) {
		let valueTwo: any =
			secondValueKey
				.split(".")
				.reduce(
					(accum, currentValue) =>
						accum ? (accum as any)[currentValue] : undefined,
					allData,
				) || "";

		return (
			<div className="flex flex-col">
				<span className={disabledClass}>
					{dataItem as string | number | boolean | T}
				</span>
				<span className={`${disabledClass} ${secondValueTextClasses}`}>
					{`${secondValueLabel}: ${valueTwo}`}
				</span>
			</div>
		);
	} else if (suffix && dataItem !== "—") {
		return <span className={disabledClass}>{`${dataItem} ${suffix}`}</span>;
	} else {
		return (
			<span className={disabledClass}>
				{dataItem as string | number | boolean | T}
			</span>
		);
	}
};

export default renderBaseDetailFieldValue;
